import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import { apiRequest } from "../../api/http";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import { fetchEmployeeDetail, updateEmployee, deleteEmployee } from "../../store/slices/employeesSlice";
import { EmployeeContractResponse, EmployeePayrollResponse, EmployeeContract, EmployeePayrollStatement, EmploymentStatus } from "../../types/admin";
import { formatDate } from "../../utils/admin";
import { getPayrollTitle, getPayrollTypeLabel, isPayrollFileOnly } from "../../utils/payroll";

const EmployeeDetailPage = () => {
  const { employeeId } = useParams();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { detail, loadingDetail, detailError, updating, deleting } = useAppSelector((state) => state.employees);

  const [contracts, setContracts] = useState<EmployeeContract[]>([]);
  const [payrolls, setPayrolls] = useState<EmployeePayrollStatement[]>([]);
  const [loadingDocs, setLoadingDocs] = useState(false);

  const [isEditing, setIsEditing] = useState(false);
  const [editStatus, setEditStatus] = useState<EmploymentStatus>("active");
  const [editWorkMinutes, setEditWorkMinutes] = useState(0);

  useEffect(() => {
    if (employeeId) {
      dispatch(fetchEmployeeDetail(Number(employeeId)));
    }
  }, [dispatch, employeeId]);

  useEffect(() => {
    if (detail && !isEditing) {
      setEditStatus(detail.employment_status);
      setEditWorkMinutes(detail.weekly_work_minutes);
    }
  }, [detail, isEditing]);

  useEffect(() => {
    const fetchDocs = async () => {
      if (!detail?.branch_id || !employeeId) return;
      
      setLoadingDocs(true);
      try {
        const [contractsRes, payrollsRes] = await Promise.all([
          apiRequest<EmployeeContractResponse>(`/admin/branches/${detail.branch_id}/employees/${employeeId}/contracts`),
          apiRequest<EmployeePayrollResponse>(`/admin/branches/${detail.branch_id}/employees/${employeeId}/payrolls`)
        ]);
        
        setContracts(contractsRes.items);
        setPayrolls(payrollsRes.items);
      } catch (error) {
        console.error("Failed to fetch employee documents:", error);
      } finally {
        setLoadingDocs(false);
      }
    };

    if (detail) {
      fetchDocs();
    }
  }, [detail, employeeId]);

  const handleUpdate = async () => {
    if (!employeeId || !detail) return;
    try {
      await dispatch(
        updateEmployee({
          employeeId: Number(employeeId),
          employment_status: editStatus !== detail.employment_status ? editStatus : undefined,
          weekly_work_minutes: editWorkMinutes !== detail.weekly_work_minutes ? editWorkMinutes : undefined,
        })
      ).unwrap();
      window.alert("직원 정보가 성공적으로 수정되었습니다.");
      setIsEditing(false);
    } catch (error) {
      window.alert(error as string || "수정에 실패했습니다.");
    }
  };

  const handleDelete = async () => {
    if (!employeeId) return;
    if (window.confirm("정말로 이 직원을 삭제하시겠습니까?")) {
      try {
        await dispatch(deleteEmployee(Number(employeeId))).unwrap();
        window.alert("직원이 삭제되었습니다.");
        navigate("/admin/employees", { replace: true });
      } catch (error) {
        window.alert(error as string || "삭제에 실패했습니다.");
      }
    }
  };

  const handleOpenPayroll = (payroll: EmployeePayrollStatement) => {
    navigate(`/admin/employees/${employeeId}/payrolls/${payroll.payroll_id}`);
  };

  return (
    <section className="admin-page">
      <header className="detail-header">
        <button className="back-btn" type="button" onClick={() => navigate(-1)}>
          ←
        </button>
        <h2 className="section-title">직원 상세정보</h2>
      </header>

      {loadingDetail && <p className="loading-text">직원 상세 정보를 불러오는 중입니다...</p>}
      {detailError && <p className="error-text">{detailError}</p>}

      {detail && (
        <>
          <div className="detail-grid wide">
            <article className="detail-card">
              <span>직원명</span>
              <strong>{detail.employee_name}</strong>
            </article>
            <article className="detail-card">
              <span>직원번호</span>
              <strong>{detail.employee_number}</strong>
            </article>
            <article className="detail-card">
              <span>연락처</span>
              <strong>{detail.phone_number}</strong>
            </article>
            <article className="detail-card">
              <span>주간 근무</span>
              {isEditing ? (
                <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                  <input 
                    type="number" 
                    value={editWorkMinutes} 
                    onChange={(e) => setEditWorkMinutes(Number(e.target.value))} 
                    style={{ width: "100px" }}
                  /> 분
                </div>
              ) : (
                <strong>{`${detail.weekly_work_label} (${detail.weekly_work_minutes}분)`}</strong>
              )}
            </article>
            <article className="detail-card">
              <span>재직 상태</span>
              {isEditing ? (
                <select value={editStatus} onChange={(e) => setEditStatus(e.target.value as EmploymentStatus)}>
                  <option value="active">재직</option>
                  <option value="retired">퇴사</option>
                </select>
              ) : (
                <strong>{detail.employment_status_label}</strong>
              )}
            </article>
            <article className="detail-card">
              <span>입사/퇴사</span>
              <strong>{`${detail.hire_date || "-"} / ${detail.resignation_date || "-"}`}</strong>
            </article>
            <article className="detail-card">
              <span>소속 지점</span>
              <strong>{`${detail.branch_name} (${detail.branch_code})`}</strong>
            </article>
            <article className="detail-card">
              <span>연동 계정</span>
              <strong>{detail.linked_user_email || "-"}</strong>
            </article>
            <article className="detail-card">
              <span>가입여부</span>
              <strong>
                <span className={`branch-status-badge ${detail.is_guest ?? (detail.linked_user_id === null) ? 'branch-status-badge--neutral' : 'branch-status-badge--operating'}`}>
                  {detail.is_guest ?? (detail.linked_user_id === null) ? '비회원' : '회원'}
                </span>
              </strong>
            </article>
          </div>

          <div className="detail-actions">
            {isEditing ? (
              <>
                <button className="primary-btn" type="button" onClick={handleUpdate} disabled={updating}>
                  {updating ? "저장 중..." : "저장하기"}
                </button>
                <button className="secondary-btn" type="button" onClick={() => setIsEditing(false)} disabled={updating}>
                  취소
                </button>
              </>
            ) : (
              <>
                <button className="primary-btn" type="button" onClick={() => setIsEditing(true)}>
                  직원정보 수정하기
                </button>
                <button className="secondary-btn danger" type="button" onClick={handleDelete} disabled={deleting}>
                  {deleting ? "삭제 중..." : "직원정보 삭제하기"}
                </button>
              </>
            )}
          </div>

          <div style={{ display: 'flex', gap: '20px', marginTop: '32px' }}>
            <div style={{ flex: 1 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                <h3 className="section-title" style={{ margin: 0 }}>계약서 목록</h3>
                <button
                  type="button"
                  style={{
                    backgroundColor: '#1d1d1f', color: 'white', padding: '8px 16px', borderRadius: '8px',
                    fontWeight: '500', fontSize: '14px', cursor: 'pointer', border: 'none'
                  }}
                  onClick={() => navigate(`/admin/employees/${employeeId}/contracts`)}
                >
                  전체보기
                </button>
              </div>
              <div className="table-shell" style={{ overflowX: 'auto' }}>
                <table className="admin-table compact" style={{ whiteSpace: 'nowrap' }}>
                  <thead>
                    <tr>
                      <th>계약서명</th>
                      <th>작성일자</th>
                      <th>상태</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loadingDocs ? (
                      <tr>
                        <td colSpan={3} className="empty-row">데이터를 불러오는 중입니다...</td>
                      </tr>
                    ) : contracts.length > 0 ? (
                      contracts.slice(0, 5).map(contract => (
                        <tr key={contract.contract_id}>
                          <td>
                            <button
                              type="button"
                              onClick={() => navigate(`/admin/employees/${employeeId}/contracts/${contract.contract_id}`)}
                              style={{ color: '#5696ff', textDecoration: 'none', border: 'none', background: 'none', cursor: 'pointer', padding: 0 }}
                            >
                              {contract.title}
                            </button>
                          </td>
                          <td>{formatDate(contract.created_at)}</td>
                          <td>{contract.status === 'completed' ? '완료' : contract.status === 'draft' ? '진행중' : contract.status}</td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={3} className="empty-row">등록된 계약서가 없습니다.</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            <div style={{ flex: 1 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                <h3 className="section-title" style={{ margin: 0 }}>급여명세서 목록</h3>
                <button
                  type="button"
                  style={{
                    backgroundColor: '#1d1d1f', color: 'white', padding: '8px 16px', borderRadius: '8px',
                    fontWeight: '500', fontSize: '14px', cursor: 'pointer', border: 'none'
                  }}
                  onClick={() => navigate(`/admin/employees/${employeeId}/payrolls`)}
                >
                  전체보기
                </button>
              </div>
              <div className="table-shell" style={{ overflowX: 'auto' }}>
                <table className="admin-table compact" style={{ whiteSpace: 'nowrap' }}>
                  <thead>
                    <tr>
                      <th>급여명세서</th>
                      <th>유형</th>
                      <th>지급액</th>
                      <th>상세</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loadingDocs ? (
                      <tr>
                        <td colSpan={4} className="empty-row">데이터를 불러오는 중입니다...</td>
                      </tr>
                    ) : payrolls.length > 0 ? (
                      payrolls.slice(0, 5).map(payroll => (
                        <tr key={payroll.payroll_id}>
                          <td>{getPayrollTitle(payroll)}</td>
                          <td>
                            <span className={`branch-status-badge ${isPayrollFileOnly(payroll) ? 'branch-status-badge--neutral' : 'branch-status-badge--operating'}`}>
                              {getPayrollTypeLabel(payroll)}
                            </span>
                          </td>
                          <td>{payroll.net_pay.toLocaleString()}원</td>
                          <td>
                            <button
                              type="button"
                              onClick={() => handleOpenPayroll(payroll)}
                              style={{ color: '#5696ff', textDecoration: 'none', border: 'none', background: 'none', cursor: 'pointer', padding: 0 }}
                            >
                              {isPayrollFileOnly(payroll) ? "파일 보기" : "명세서 보기"}
                            </button>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={4} className="empty-row">등록된 급여명세서가 없습니다.</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </>
      )}
    </section>
  );
};

export default EmployeeDetailPage;
